import type { FicheSection } from '@pharmafiche/schema';

export type LayoutGroup =
  | { kind: 'row'; cols: number; sections: FicheSection[] }
  | { kind: 'full'; section: FicheSection };

/** Section types that occupy their own full-width row. */
const FULL_WIDTH = new Set<FicheSection['type']>([
  'diagram',
  'comparison_table',
  'mnemonic',
  'warning',
]);

/** Section types that can be packed into a horizontal row of drug cards (5 wide). */
const DRUG_CARD = new Set<FicheSection['type']>(['drug_card']);

/** Section types that can be packed into rows of 2–3 small cards. */
const SMALL_CARD = new Set<FicheSection['type']>([
  'intro',
  'classification',
  'clinical_case',
  'free_text',
]);

/**
 * Walk sections left-to-right and group them by visual width.
 *
 *  - Consecutive `drug_card` sections fold into a `row-5`.
 *  - 2 consecutive small cards fold into a `row-2`; 3+ into `row-3` (capped at 3 per row).
 *  - Wide sections (diagram, mnemonic, warning, comparison_table) emit alone.
 *
 * Determinism: same input always yields the same grouping. Phase 1's antiplatelet
 * sample produces exactly the prototype's layout (2 + diagram + 5 + 3 + mnemo + warn).
 */
export function groupSections(sections: FicheSection[]): LayoutGroup[] {
  const out: LayoutGroup[] = [];
  let i = 0;
  while (i < sections.length) {
    const s = sections[i];
    if (!s) break;

    if (FULL_WIDTH.has(s.type)) {
      out.push({ kind: 'full', section: s });
      i++;
      continue;
    }

    if (DRUG_CARD.has(s.type)) {
      const run: FicheSection[] = [];
      while (i < sections.length) {
        const nxt = sections[i];
        if (!nxt || !DRUG_CARD.has(nxt.type)) break;
        run.push(nxt);
        i++;
      }
      // Emit in chunks of up to 5; cols adapts to the chunk size so 2 cards
      // span a row of 2, 3 span a row of 3, etc.
      for (let k = 0; k < run.length; k += 5) {
        const chunk = run.slice(k, k + 5);
        const cols = Math.max(2, Math.min(chunk.length, 5));
        out.push({ kind: 'row', cols, sections: chunk });
      }
      continue;
    }

    if (SMALL_CARD.has(s.type)) {
      const run: FicheSection[] = [];
      while (i < sections.length) {
        const nxt = sections[i];
        if (!nxt || !SMALL_CARD.has(nxt.type)) break;
        run.push(nxt);
        i++;
      }
      // 1 → single full-ish; 2 → row-2; 3+ → row-3 (chunked)
      if (run.length === 1) {
        out.push({ kind: 'row', cols: 2, sections: run });
      } else if (run.length === 2) {
        out.push({ kind: 'row', cols: 2, sections: run });
      } else {
        for (let k = 0; k < run.length; k += 3) {
          out.push({ kind: 'row', cols: 3, sections: run.slice(k, k + 3) });
        }
      }
      continue;
    }

    // Fallback: emit alone
    out.push({ kind: 'full', section: s });
    i++;
  }
  return out;
}
